/**
 * Universal Notification System - localStorage Utilities
 * 
 * This module handles client-side storage for notifications until backend APIs are available.
 * Uses localStorage to persist notification history, read status, and preferences.
 */

import {
  STORAGE_KEYS,
  MAX_NOTIFICATIONS,
} from '../types/notifications';

const NOTIFICATION_STORAGE_KEY = STORAGE_KEYS.NOTIFICATIONS;
const NOTIFICATION_READ_STATUS_KEY = 'admin_notification_read_status';
const NOTIFICATION_PREFERENCES_KEY = 'admin_notification_preferences';
const MAX_NOTIFICATIONS_IN_STORAGE = MAX_NOTIFICATIONS;

/**
 * Get all notifications from localStorage
 * @returns {Array} Array of notification objects
 */
export const getNotifications = () => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting notifications from localStorage:', error);
    return [];
  }
};

/**
 * Save notifications to localStorage
 * @param {Array} notifications - Array of notification objects to save
 */
export const saveNotifications = (notifications) => {
  try {
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(notifications));
  } catch (error) {
    console.error('Error saving notifications to localStorage:', error);
  }
};

/**
 * Add a new notification to storage
 * Maintains max limit of MAX_NOTIFICATIONS_IN_STORAGE
 * @param {Object} notification - Notification object to add
 */
export const addNotification = (notification) => {
  try {
    const notifications = getNotifications();
    
    // Check if notification already exists (by id)
    const existingIndex = notifications.findIndex(n => n.id === notification.id);
    
    if (existingIndex !== -1) {
      // Update existing notification
      notifications[existingIndex] = notification;
    } else {
      // Add new notification to beginning
      notifications.unshift(notification);
    }
    
    // Trim to max limit
    const trimmed = notifications.slice(0, MAX_NOTIFICATIONS_IN_STORAGE);
    
    saveNotifications(trimmed);
    return trimmed;
  } catch (error) {
    console.error('Error adding notification to localStorage:', error);
    return getNotifications();
  }
};

/**
 * Alias for addNotification - for compatibility with existing code
 * @param {Object} notification - Notification object to store
 */
export const storeNotification = addNotification;

/**
 * Update an existing notification
 * @param {string} notificationId - ID of notification to update
 * @param {Object} updates - Fields to update
 */
export const updateNotification = (notificationId, updates) => {
  try {
    const notifications = getNotifications();
    const index = notifications.findIndex(n => n.id === notificationId);
    
    if (index !== -1) {
      notifications[index] = { ...notifications[index], ...updates };
      saveNotifications(notifications);
      return notifications[index];
    }
    
    return null;
  } catch (error) {
    console.error('Error updating notification in localStorage:', error);
    return null;
  }
};

/**
 * Delete a notification from storage
 * @param {string} notificationId - ID of notification to delete
 */
export const deleteNotification = (notificationId) => {
  try {
    const notifications = getNotifications();
    const filtered = notifications.filter(n => n.id !== notificationId);
    saveNotifications(filtered);
    return filtered;
  } catch (error) {
    console.error('Error deleting notification from localStorage:', error);
    return getNotifications();
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - ID of notification to mark as read
 */
export const markAsRead = (notificationId) => {
  try {
    const notifications = getNotifications();
    const index = notifications.findIndex(n => n.id === notificationId);
    
    if (index !== -1) {
      notifications[index].isRead = true;
      notifications[index].readAt = new Date().toISOString();
      saveNotifications(notifications);
    }
    
    return notifications;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return getNotifications();
  }
};

/**
 * Alias for markAsRead - for compatibility with existing code
 * @param {string} notificationId - ID of notification to mark as read
 */
export const markNotificationAsRead = markAsRead;

/**
 * Mark notification as unread
 * @param {string} notificationId - ID of notification to mark as unread
 */
export const markAsUnread = (notificationId) => {
  try {
    const notifications = getNotifications();
    const index = notifications.findIndex(n => n.id === notificationId);
    
    if (index !== -1) {
      notifications[index].isRead = false;
      notifications[index].readAt = null;
      saveNotifications(notifications);
    }
    
    return notifications;
  } catch (error) {
    console.error('Error marking notification as unread:', error);
    return getNotifications();
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = () => {
  try {
    const notifications = getNotifications();
    const updated = notifications.map(n => ({
      ...n,
      isRead: true,
      readAt: new Date().toISOString(),
    }));
    saveNotifications(updated);
    return updated;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return getNotifications();
  }
};

/**
 * Get unread count from localStorage
 * @returns {number} Number of unread notifications
 */
export const getUnreadCount = () => {
  try {
    const notifications = getNotifications();
    return notifications.filter(n => !n.isRead).length;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Get notifications by category
 * @param {string} category - Category to filter by
 * @returns {Array} Filtered notifications
 */
export const getNotificationsByCategory = (category) => {
  try {
    const notifications = getNotifications();
    return notifications.filter(n => n.category === category);
  } catch (error) {
    console.error('Error getting notifications by category:', error);
    return [];
  }
};

/**
 * Get notifications by priority
 * @param {string} priority - Priority to filter by
 * @returns {Array} Filtered notifications
 */
export const getNotificationsByPriority = (priority) => {
  try {
    const notifications = getNotifications();
    return notifications.filter(n => n.priority === priority);
  } catch (error) {
    console.error('Error getting notifications by priority:', error);
    return [];
  }
};

/**
 * Get read status map from localStorage
 * @returns {Object} Map of notification IDs to read status
 */
export const getReadStatusMap = () => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_READ_STATUS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error getting read status map:', error);
    return {};
  }
};

/**
 * Save read status map to localStorage
 * @param {Object} statusMap - Map of notification IDs to read status
 */
export const saveReadStatusMap = (statusMap) => {
  try {
    localStorage.setItem(NOTIFICATION_READ_STATUS_KEY, JSON.stringify(statusMap));
  } catch (error) {
    console.error('Error saving read status map:', error);
  }
};

/**
 * Update read status for a notification
 * @param {string} notificationId - ID of notification
 * @param {boolean} isRead - Read status
 */
export const updateReadStatus = (notificationId, isRead) => {
  try {
    const statusMap = getReadStatusMap();
    statusMap[notificationId] = {
      isRead,
      readAt: isRead ? new Date().toISOString() : null,
    };
    saveReadStatusMap(statusMap);
  } catch (error) {
    console.error('Error updating read status:', error);
  }
};

/**
 * Get notification preferences from localStorage
 * @returns {Object} User notification preferences
 */
export const getNotificationPreferences = () => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_PREFERENCES_KEY);
    return stored ? JSON.parse(stored) : {
      enabled: true,
      soundEnabled: true,
      toastEnabled: true,
      desktopNotifications: false,
      categories: {},
    };
  } catch (error) {
    console.error('Error getting notification preferences:', error);
    return {
      enabled: true,
      soundEnabled: true,
      toastEnabled: true,
      desktopNotifications: false,
      categories: {},
    };
  }
};

/**
 * Save notification preferences to localStorage
 * @param {Object} preferences - User notification preferences
 */
export const saveNotificationPreferences = (preferences) => {
  try {
    localStorage.setItem(NOTIFICATION_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error saving notification preferences:', error);
  }
};

/**
 * Clear all notifications from localStorage
 */
export const clearAllNotifications = () => {
  try {
    localStorage.removeItem(NOTIFICATION_STORAGE_KEY);
    localStorage.removeItem(NOTIFICATION_READ_STATUS_KEY);
    localStorage.removeItem(STORAGE_KEYS.UNREAD_COUNT);
    localStorage.removeItem(STORAGE_KEYS.LAST_SYNC);
  } catch (error) {
    console.error('Error clearing notifications:', error);
  }
};

/**
 * Get notification by ID
 * @param {string} notificationId - ID of notification to get
 * @returns {Object|null} Notification object or null
 */
export const getNotificationById = (notificationId) => {
  try {
    const notifications = getNotifications();
    return notifications.find(n => n.id === notificationId) || null;
  } catch (error) {
    console.error('Error getting notification by ID:', error);
    return null;
  }
};

/**
 * Clear old notifications (older than specified days)
 * @param {number} days - Number of days to keep
 */
export const clearOldNotifications = (days = 30) => {
  try {
    const notifications = getNotifications();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const filtered = notifications.filter(n => {
      const notificationDate = new Date(n.createdAt);
      return notificationDate > cutoffDate;
    });
    
    saveNotifications(filtered);
    return filtered;
  } catch (error) {
    console.error('Error clearing old notifications:', error);
    return getNotifications();
  }
};

